import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spin,
  Button,
  message,
  Collapse,
  Tooltip,
  Tag,
  Card,
  Row,
  Col,
  Typography,
  Select,
  Menu,
  Modal,
  Space,
  Dropdown,
} from "antd";
import moment from "moment";
import { usePermissions } from "../../PermissionsProvider";
import {
  EvaluateResult,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ChatMessage,
  EvaluateResponse,
  Turn,
  EvaluateRequest,
} from "../../model_gen/chat";
import {
  CopyOutlined,
  PlayCircleOutlined,
  RightOutlined,
  LeftOutlined,
  PlusOutlined,
  EditOutlined,
  CloseOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { SentinelService } from "../../services/SentinelService";
import { Sentinel } from "../../model_gen/sentinel";
import { v4 as uuidv4 } from "uuid";
import { ConfigService } from "../../services/ConfigService";
import { ModelConfig } from "../../model_gen/config";
import { RequestsService } from "../../services/RequestsService";
import EditSentinelForm from "../Sentinel/EditSentinelForm";
import { EvaluationService } from "../../services/EvaluationService";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { TestSetService } from "../../services/TestSetService";
import RequestOverview from "./RequestOverview";
import { IntentGroup } from "../../model_gen/intent";
import { IntentService } from "../../services/IntentService";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface RequestOverviewProps {
  // Add any props if needed
}

interface ReRun {
  id: string;
  messages: ChatMessage[];
  state: "pre-run" | "post-run";
  model?: string;
  metrics?: {
    time: number;
    tokens: {
      prompt: number;
      completion: number;
    };
    cost: number;
  };
  originalRequest: ChatCompletionRequest;
  response?: ChatCompletionResponse;
}

interface EvaluationBlock {
  id: string;
  selectedSentinels: string[];
  editedSentinels: { [key: string]: Sentinel };
  versionId: string;
  hasRun: boolean;
  evaluationResponse?: EvaluateResponse;
  evaluationRunId?: number;
  evaluationRequestId?: string;
}

const RequestOverviewScreen: React.FC<RequestOverviewProps> = () => {
  const { apiKey, companyId } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingRerun, setLoadingRerun] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [request, setRequest] = useState<ChatCompletionRequest | null>(null);
  const [response, setResponse] = useState<ChatCompletionResponse | null>(null);
  const { sessionId, applicationId, requestId } = useParams<{
    sessionId: string;
    applicationId: string;
    requestId: string;
  }>();
  const [evaluationBlocks, setEvaluationBlocks] = useState<EvaluationBlock[]>(
    [],
  );
  const [reruns, setReruns] = useState<ReRun[]>([]);
  const [editingMessage, setEditingMessage] = useState<{
    rerunId: string;
    index: number;
  } | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string>("original");
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [isEditSentinelModalVisible, setIsEditSentinelModalVisible] =
    useState(false);
  const selectedSentinelRef = useRef<Sentinel | undefined>(undefined);
  const [, forceUpdate] = useState({});
  const [originalSentinels, setOriginalSentinels] = useState<Sentinel[]>([]);
  const [selectKey, setSelectKey] = useState(0);
  const [runningEvaluations, setRunningEvaluations] = useState<{
    [key: string]: boolean;
  }>({});
  const navigate = useNavigate();
  const [loadingUrls, setLoadingUrls] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [dropdownMenus, setDropdownMenus] = useState<{
    [key: string]: React.ReactElement;
  }>({});
  const focusedEvaluationBlockRef = useRef<EvaluationBlock | null>(null);
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);

  useEffect(() => {
    if (applicationId && requestId) {
      setIsLoading(true);
      loadRequest();
    }
  }, [applicationId, requestId, apiKey]);

  useEffect(() => {
    if (request?.action_type) {
      loadSentinels(request.action_type);
    }
  }, [request]);

  useEffect(() => {
    console.log(`trying to load intent group with applicationId: ${applicationId} and applicationActionId: ${request?.application_action_id}`);
    if (apiKey && applicationId && request?.application_action_id) {
      console.log("loading intent group");
      loadIntentGroup(Number(applicationId), request.application_action_id);
    }
  }, [apiKey, applicationId, request]);

  useEffect(() => {
    if (intentGroup) {
      ConfigService.getModels(intentGroup?.id ?? -1, () => {}, apiKey).subscribe(
        (models: ModelConfig) => {
          const serverModels = Array.from(new Set(models.server_models)).sort();
          setModelOptions(serverModels);
        },
        (error: any) => {
          console.error("Error fetching models:", error);
        },
      );
    }
  }, [apiKey, intentGroup]);

  useEffect(() => {
    if (applicationId && sessionId && requestId) {
      setDropdownMenus({});
      setLoadingUrls({});
      setEvaluationBlocks([]);
      loadRequest();
      loadExistingReruns();
      loadExistingEvaluations();
    }
  }, [sessionId, applicationId, requestId, apiKey]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);

  const loadRequest = () => {
    setIsLoading(true);
    // First get the request to get the application_action_id
    RequestsService.getRequestById(requestId!, () => {}, apiKey).subscribe(
      (req: ChatCompletionRequest) => {
        if (req) {
          setRequest(req);
          RequestsService.getRequestResponseById(requestId, () => {}, apiKey).subscribe(
            (response: ChatCompletionResponse) => {
              setResponse(response);
              setIsLoading(false);
            },
            (error: any) => {
              console.error("Error fetching response:", error);
              setIsLoading(false);
            },
          );
        } else {
          message.error("Failed to load request - no request data found");
          setIsLoading(false);
        }
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
        message.error("Failed to load request. Please try again.");
      },
    );
  };

  const loadIntentGroup = (applicationId: number, applicationActionId: number) => {
    IntentService.getIntentGroupByApplicationAndIntent(applicationId, applicationActionId, () => {}, apiKey).subscribe(
      (intentGroup: IntentGroup) => {
        setIntentGroup(intentGroup);  
      },
      (error: any) => {
        console.error("Error fetching intent group:", error);
      },
    );
  };

  const loadSentinels = (actionType: string) => {
    if (applicationId) {
      SentinelService.getSentinelsByIntentName(
        actionType,
        null,
        apiKey,
      ).subscribe(
        (fetchedSentinels: Sentinel[]) => {
          setOriginalSentinels(Array.isArray(fetchedSentinels) ? fetchedSentinels : []);
        },
        (error: any) => {
          console.error("Error fetching sentinels:", error);
          setOriginalSentinels([]);
          message.error("Failed to load sentinels");
        },
      );
    } else {
      setOriginalSentinels([]);
    }
  };

  const copyRequestId = (id: string) => {
    navigator.clipboard.writeText(id);
    message.success("Request ID copied to clipboard");
  };

  const handleSentinelChange = (value: string[], blockId: string) => {
    setEvaluationBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              selectedSentinels: value,
              editedSentinels: value.reduce(
                (acc, sentinelId) => {
                  if (block.editedSentinels[sentinelId]) {
                    acc[sentinelId] = block.editedSentinels[sentinelId];
                  }
                  return acc;
                },
                {} as { [key: string]: Sentinel },
              ),
            }
          : block,
      ),
    );
  };

  const handleRunEvaluation = (blockId: string) => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    if (block) {
      const finalSentinels = block.selectedSentinels.map((sentinelId) =>
        getFinalSentinelForBlock(blockId, sentinelId),
      );
      setRunningEvaluations((prev) => ({ ...prev, [blockId]: true }));

      // Get the request that this evaluation block references
      let evaluationRequest: ChatCompletionRequest;
      if (block.versionId === "original") {
        if (!request) {
          console.error("Original request not found");
          return;
        }
        // Create a deep copy of the request
        evaluationRequest = JSON.parse(
          JSON.stringify(request),
        ) as ChatCompletionRequest;
        // Add the assistant's response to the messages for evaluation
        if (request?.params?.messages?.[request?.params?.messages?.length - 1].role === "assistant") {
          evaluationRequest.params.messages = [
            ...(evaluationRequest.params.messages || []),
            JSON.parse(JSON.stringify(request.params.messages[request.params.messages.length - 1])),
          ];
        }
      } else {
        const rerun = reruns.find((r) => r.id === block.versionId);
        if (!rerun) {
          console.error("Rerun not found");
          return;
        }
        // Create a deep copy of the request
        evaluationRequest = JSON.parse(
          JSON.stringify(rerun.originalRequest),
        ) as ChatCompletionRequest;
        evaluationRequest.params.messages = JSON.parse(JSON.stringify(rerun.messages));
      }

      EvaluationService.runOneOffEvaluation(
        evaluationRequest,
        finalSentinels,
        null,
        apiKey,
      ).subscribe(
        (response: EvaluateResponse) => {
          setRunningEvaluations((prev) => ({ ...prev, [blockId]: false }));
          setEvaluationBlocks((prev) =>
            prev.map((b) =>
              b.id === blockId
                ? { ...b, hasRun: true, evaluationResponse: response }
                : b,
            ),
          );
        },
        (error: any) => {
          console.error("Error running evaluation:", error);
          setRunningEvaluations((prev) => ({ ...prev, [blockId]: false }));
          message.error("Failed to run evaluation. Please try again.");
        },
      );
    }
  };

  const handleAddEvaluation = () => {
    const highSeveritySentinels = originalSentinels
      .filter((sentinel) => sentinel.severity >= 3)
      .map((sentinel) => sentinel.id.toString());

    setEvaluationBlocks((prev) => [
      ...prev,
      {
        id: uuidv4(),
        selectedSentinels: highSeveritySentinels,
        editedSentinels: {},
        versionId: selectedVersion,
        hasRun: false,
        evaluationRequestId: `manual_${uuidv4()}`,
      },
    ]);
  };

  const handleRerunRequest = () => {
    if (!request) {
      console.error("Cannot create rerun: request not found");
      return;
    }

    if (!modelOptions.length) {
      message.error("Model options are not loaded yet. Please try again in a moment.");
      return;
    }

    const originalRequest = request;
    
    // Get all messages from the original request
    const originalMessages = originalRequest.params.messages || [];

    // Create a new rerun with all original messages except the last one if it's an assistant message
    const newRerun: ReRun = {
      id: `Rerun ${reruns.length + 1}`,
      messages: originalMessages,  // Take all messages except the last one
      state: "pre-run",
      model: originalRequest.params.model,
      originalRequest: {
        ...originalRequest,
        params: {
          ...originalRequest.params,
          messages: originalMessages  // Update the request messages too
        }
      }
    };

    console.log("New rerun messages:", newRerun.messages);
    
    setReruns((prevReruns) => [...prevReruns, newRerun]);
    setSelectedVersion(newRerun.id);
  };

  const handleModelChange = (rerunId: string, newModel: string) => {
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId ? { ...rerun, model: newModel } : rerun,
      ),
    );
  };

  const handleEditMessage = (
    rerunId: string,
    index: number,
    newContent: string,
  ) => {
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId
          ? {
              ...rerun,
              messages: rerun.messages.map((msg, i) =>
                i === index ? { ...msg, content: newContent } : msg,
              ),
            }
          : rerun,
      ),
    );
  };

  const renderVersionContent = () => {
    if (selectedVersion === "original") {
      return renderOriginalRequest();
    } else {
      const rerun = reruns.find((r) => r.id === selectedVersion);
      return rerun ? renderRerunRequest(rerun) : null;
    }
  };

  const renderOriginalRequest = () => {
    if (!request) {
      return <Paragraph>Request not found</Paragraph>;
    }

    return (
      <RequestOverview
        request={request}
        response={response}
        isEditable={false}
      />
    );
  };

  const handleRequestRerun = (rerun: ReRun) => {
    setLoadingRerun((prev) => ({ ...prev, [rerun.id]: true }));
    const newRequest: ChatCompletionRequest = rerun.originalRequest;
    newRequest.params.messages = rerun.messages;
    newRequest.params.model = rerun.model;
    newRequest.fallback_model = null;
    newRequest.preserve_model_alias = true;
    if (newRequest.metadata) {
      newRequest.metadata["rerun"] = "true";
      newRequest.metadata["original_request_id"] = requestId;
    } else {
      newRequest.metadata = { rerun: "true", original_request_id: requestId };
    }
    RequestsService.getChatCompletionRerun(
      () => {},
      newRequest,
      apiKey,
    ).subscribe(
      (response: ChatCompletionResponse) => {
        setLoadingRerun((prev) => ({ ...prev, [rerun.id]: false }));
        console.log("Rerun response:", response);
        // Update the rerun state with the response
        setReruns((prevReruns) =>
          prevReruns.map((r) => {
            if (r.id === rerun.id) {
              return {
                ...r,
                state: "post-run",
                model: response.model,
                metrics: {
                  time: response.response_time,
                  tokens: {
                    prompt: response.usage.prompt_tokens,
                    completion: response.usage.completion_tokens,
                  },
                  cost: response.usage.cost,
                },
                messages: [
                  ...r.messages
                ],
                response: response,
              };
            }
            return r;
          }),
        );
      },
      (error: any) => {
        console.error(`Rerun error: ${JSON.stringify(error, null, 2)}`);
        setLoadingRerun((prev) => ({ ...prev, [rerun.id]: false }));
        message.error("Failed to rerun the request. Please try again.");
      },
    );
  };

  const renderRerunRequest = (rerun: ReRun) => {
    return (
      <div style={{ marginBottom: "24px" }}>
        {loadingRerun[rerun.id] ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <RequestOverview
              request={{
                ...rerun.originalRequest,
                params: {
                  ...rerun.originalRequest.params,
                  messages: rerun.messages,
                  model: rerun.model,
                },
              }}
              response={rerun.response}
              isEditable={rerun.state === "pre-run"}
              onMessageEdit={(index, content) =>
                handleEditMessage(rerun.id, index, content)
              }
              onModelChange={(model) => handleModelChange(rerun.id, model)}
              modelOptions={modelOptions}
              editingMessageIndex={editingMessage?.rerunId === rerun.id ? editingMessage.index : null}
              onEditingMessageChange={(index) =>
                setEditingMessage(index !== null ? { rerunId: rerun.id, index } : null)
              }
              editMode={rerun.state === "pre-run" ? "all" : "final-assistant"}
            />
            {rerun.state === "pre-run" && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => handleRequestRerun(rerun)}
                loading={loadingRerun[rerun.id]}
                style={{ width: "100%", marginTop: "16px" }}>
                Run Request
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  const loadExistingReruns = () => {
    RequestsService.getRequestReruns(
      sessionId!,
      applicationId!,
      requestId!,
      null,
      apiKey,
    ).subscribe(
      (fetchedReruns: Turn[]) => {
        const convertedReruns: ReRun[] = fetchedReruns.map(
          (turn: Turn, index: number) => {
            // Use the request messages directly - they already contain the response
            const messages = turn.request?.params?.messages || [];
            
            const rerun: ReRun = {
              id: `Rerun ${index + 1}`,
              messages: messages.map(msg => ({ ...msg })), // Create a deep copy of messages
              state: "post-run" as const,
              model: turn.response?.model,
              metrics: {
                time: turn.response?.response_time || 0,
                tokens: {
                  prompt: turn.response?.usage?.prompt_tokens || 0,
                  completion: turn.response?.usage?.completion_tokens || 0,
                },
                cost: turn.response?.usage?.cost || 0,
              },
              originalRequest: turn.request,
              response: turn.response,
            };
            
            console.log("Created rerun:", {
              id: rerun.id,
              messages: rerun.messages,
            });
            
            return rerun;
          }
        );
        setReruns(convertedReruns);
      },
      (error: any) => {
        console.error("Error fetching existing reruns:", error);
        message.error("Failed to load existing reruns");
      },
    );
  };

  const selectedRerun = reruns.find((r) => r.id === selectedVersion);

  const handleEditSentinel = (
    e: React.MouseEvent,
    block: EvaluationBlock,
    sentinelId: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEvaluationRunning()) return; // Prevent editing if any evaluation is running

    if (!block) {
      console.error("Evaluation block not found for sentinel:", sentinelId);
      message.error("Unable to edit: Evaluation block not found.");
      return;
    }

    const sentinel =
      block.editedSentinels[sentinelId] ||
      originalSentinels.find((s) => s.id.toString() === sentinelId);
    if (sentinel) {
      selectedSentinelRef.current = sentinel;
      focusedEvaluationBlockRef.current = block;
      setIsEditSentinelModalVisible(true);
      forceUpdate({}); // Force a re-render
    } else {
      console.error("Sentinel not found for id:", sentinelId);
      message.error("Unable to edit: Sentinel not found.");
    }
  };

  const handleEditSentinelSubmit = (formData: Sentinel) => {
    if (formData.id === undefined) {
      console.error("Sentinel ID is undefined in formData:", formData);
      message.error("Unable to save changes: Sentinel ID is missing.");
      return;
    }

    setEvaluationBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.selectedSentinels.includes(formData.id.toString())) {
          return {
            ...block,
            editedSentinels: {
              ...block.editedSentinels,
              [formData.id]: formData,
            },
          };
        }
        return block;
      }),
    );
    setSelectKey((prevKey) => prevKey + 1); // Force Select to re-render
    message.success("Sentinel updated successfully.");

    // Close the modal
    setIsEditSentinelModalVisible(false);
    focusedEvaluationBlockRef.current = null;
    selectedSentinelRef.current = undefined;
  };

  const getFinalSentinelForBlock = (
    blockId: string,
    sentinelId: string,
  ): Sentinel => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    return (
      block?.editedSentinels[sentinelId] ||
      originalSentinels.find((s) => s.id.toString() === sentinelId) ||
      ({} as Sentinel)
    );
  };

  const isSentinelEdited = (blockId: string, sentinelId: string): boolean => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const editedSentinel = block?.editedSentinels[sentinelId];
    if (!editedSentinel) return false;

    const originalSentinel = originalSentinels.find(
      (s) => s.id.toString() === sentinelId,
    );
    if (!originalSentinel) return false;

    return (
      editedSentinel.sentinel_name !== originalSentinel.sentinel_name ||
      editedSentinel.sentinel_directive.directive_body.main !==
        originalSentinel.sentinel_directive.directive_body.main ||
      editedSentinel.sentinel_directive.directive_body.override !==
        originalSentinel.sentinel_directive.directive_body.override ||
      !arraysEqual(
        editedSentinel.action_types,
        originalSentinel.action_types,
      ) ||
      editedSentinel.severity !== originalSentinel.severity ||
      !objectsEqual(
        editedSentinel.meta as { [key: string]: string },
        originalSentinel.meta as { [key: string]: string },
      )
    );
  };

  // Helper functions
  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, index) => val === b[index]);

  const objectsEqual = (
    a: { [key: string]: string },
    b: { [key: string]: string },
  ) =>
    Object.keys(a).length === Object.keys(b).length &&
    Object.keys(a).every((key) => a[key] === b[key]);

  const getSentinelName = (blockId: string, sentinelId: string): string => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const editedSentinel = block?.editedSentinels[sentinelId];
    if (editedSentinel) {
      return editedSentinel.sentinel_name;
    }
    const originalSentinel = originalSentinels.find(
      (s) => s.id.toString() === sentinelId,
    );
    return originalSentinel ? originalSentinel.sentinel_name : "";
  };

  const isLastMessageAssistant = () => {
    if (selectedVersion === "original") {
      const responseContent = request?.params?.messages?.[request?.params?.messages?.length - 1].role === "assistant" ? request?.params?.messages?.[request?.params?.messages?.length - 1].content : null;
      return responseContent !== undefined && responseContent !== "";
    } else {
      const rerun = reruns.find((r) => r.id === selectedVersion);
      return (
        rerun?.messages.length > 0 &&
        rerun.messages[rerun.messages.length - 1].role === "assistant"
      );
    }
  };

  const renderSelectedSentinel = (blockId: string) => (props: any) => {
    const { value, closable, onClose } = props;
    const isEdited = isSentinelEdited(blockId, value);
    const sentinelName = getSentinelName(blockId, value);
    const isRunning = runningEvaluations[blockId] || isEvaluationRunning();
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const hasRun = block?.hasRun || false;

    return (
      <Tag
        closable={closable && !isRunning}
        onClose={onClose}
        style={{ marginRight: 3 }}
        onClick={(e) => e.preventDefault()}>
        <span style={{ marginRight: 8 }}>
          {sentinelName}
          {isEdited && (
            <span
              style={{
                color: "#21b892",
                fontWeight: "bold",
                fontSize: "16px",
                marginLeft: "2px",
              }}>
              *
            </span>
          )}
        </span>
        <Button
          icon={hasRun ? <SearchOutlined /> : <EditOutlined />}
          size="small"
          type="text"
          onClick={(e) => handleEditSentinel(e, block, value)}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          disabled={isRunning}
          style={{ cursor: isRunning ? "not-allowed" : "pointer" }}
        />
      </Tag>
    );
  };

  const hasUnrunEvaluations = () => {
    return evaluationBlocks
      .filter((block) => block.versionId === selectedVersion)
      .some((block) => !block.hasRun); // Assuming we add a 'hasRun' property to EvaluationBlock
  };

  const isEvaluationRunning = () =>
    Object.values(runningEvaluations).some((running) => running);

  const renderEvaluationResult = (evaluationResponse: EvaluateResponse) => {
    if (
      !evaluationResponse.evaluation_results ||
      evaluationResponse.evaluation_results.length === 0
    ) {
      return (
        <Space>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {`${evaluationResponse.evaluation_time}ms`}
          </Text>
          <Tooltip title="No evaluation results">
            <MinusCircleFilled style={{ fontSize: "24px", color: "#bfbfbf" }} />
          </Tooltip>
        </Space>
      );
    }

    const hasFault = evaluationResponse.evaluation_results.some(
      (result) => result.status === "FAULT",
    );

    return (
      <Space>
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {`${evaluationResponse.evaluation_time}ms`}
        </Text>
        <Tooltip title={hasFault ? "Evaluation failed" : "Evaluation passed"}>
          {hasFault ? (
            <CloseCircleFilled style={{ fontSize: "24px", color: "#ff4d4f" }} />
          ) : (
            <CheckCircleFilled style={{ fontSize: "24px", color: "#52c41a" }} />
          )}
        </Tooltip>
      </Space>
    );
  };

  const handleViewRequest = (
    evaluationRequestId: string,
    sentinelId: string,
  ) => {
    const key = `${evaluationRequestId}:${sentinelId}`;

    const menu = (
      <Menu>
        <Menu.Item key="loading" disabled>
          Loading...
        </Menu.Item>
      </Menu>
    );

    const fetchUrls = (visible: boolean) => {
      if (visible && !dropdownMenus[key]) {
        setLoadingUrls((prev) => ({ ...prev, [key]: true }));
        EvaluationService.getEvaluationRequestUrls(
          evaluationRequestId,
          sentinelId,
          null,
          apiKey,
        ).subscribe(
          (response: any[]) => {
            const updatedMenu = (
              <Menu>
                {response.map((item, index) => (
                  <Menu.Item
                    key={index}
                    onClick={() => {
                      // Navigate to the URL and clear the state
                      navigate(item.url, { state: {} });
                    }}>
                    {item.request_type}
                  </Menu.Item>
                ))}
              </Menu>
            );
            setDropdownMenus((prev) => ({ ...prev, [key]: updatedMenu }));
            setLoadingUrls((prev) => ({ ...prev, [key]: false }));
          },
          (error: any) => {
            console.error("Error fetching evaluation request URLs:", error);
            message.error("Failed to fetch request details. Please try again.");
            setLoadingUrls((prev) => ({ ...prev, [key]: false }));
          },
        );
      }
    };

    return (
      <Dropdown
        overlay={dropdownMenus[key] || menu}
        trigger={["click"]}
        onVisibleChange={fetchUrls}>
        <Button loading={loadingUrls[key]}>View Request</Button>
      </Dropdown>
    );
  };

  const loadExistingEvaluations = () => {
    EvaluationService.getEvaluationRequests(
      applicationId!,
      sessionId!,
      requestId!,
      null,
      apiKey,
    ).subscribe(
      (requests: EvaluateRequest[]) => {
        const newBlocks: EvaluationBlock[] = requests
          .filter((request) => request.eval_results_set)
          .map((request) => {
            const sentinelIds = request.eval_results_set.evaluation_results.map(
              (result: EvaluateResult) => result.sentinel_id.toString(),
            );

            return {
              id: uuidv4(),
              selectedSentinels: sentinelIds,
              editedSentinels: {},
              versionId: "original",
              hasRun: true,
              evaluationResponse: request.eval_results_set,
              evaluationRunId: request.evaluation_run_id,
              evaluationRequestId: request.evaluation_request_id,
            };
          });

        setEvaluationBlocks(newBlocks);
      },
      (error: any) => {
        console.error("Error fetching existing evaluations:", error);
        message.error("Failed to load existing evaluations");
      },
    );
  };

  const renderEvaluationBlocks = () => {
    return evaluationBlocks
      .filter((block) => block.versionId === selectedVersion)
      .map((block) => (
        <Col span={24} key={block.id}>
          <Collapse>
            <Panel
              header={
                <Row gutter={[16, 16]} align="middle" style={{ width: "100%" }}>
                  <Col flex="auto">
                    <Space>
                      {!block.hasRun ? (
                        <Tag color="orange">Manual</Tag>
                      ) : block.evaluationRunId && block.evaluationRunId > 0 ? (
                        <Tag
                          color="purple"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/evaluation/${block.evaluationRunId}`);
                          }}>
                          Batch Eval
                        </Tag>
                      ) : block.evaluationRequestId?.startsWith("manual_") ? (
                        <Tag color="orange">Manual</Tag>
                      ) : (
                        <Tag color="blue">Real-time</Tag>
                      )}
                      <Select
                        key={selectKey}
                        mode="multiple"
                        style={{ minWidth: "200px", maxWidth: "100%" }}
                        placeholder="Select Sentinels"
                        value={block.selectedSentinels}
                        onChange={(value) =>
                          handleSentinelChange(value, block.id)
                        }
                        optionLabelProp="label"
                        tagRender={renderSelectedSentinel(block.id)}
                        disabled={block.evaluationResponse !== undefined}>
                        {originalSentinels.map((sentinel) => (
                          <Select.Option
                            key={sentinel.id}
                            value={sentinel.id.toString()}
                            label={sentinel.sentinel_name}>
                            {sentinel.sentinel_name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>
                  <Col>
                    {block.evaluationResponse ? (
                      renderEvaluationResult(block.evaluationResponse)
                    ) : (
                      <Button
                        type="primary"
                        icon={
                          runningEvaluations[block.id] ? (
                            <LoadingOutlined />
                          ) : (
                            <PlayCircleOutlined />
                          )
                        }
                        onClick={() => handleRunEvaluation(block.id)}
                        disabled={
                          block.selectedSentinels.length === 0 ||
                          runningEvaluations[block.id] ||
                          isEvaluationRunning()
                        }>
                        {runningEvaluations[block.id] ? "Running" : "Run"}
                      </Button>
                    )}
                  </Col>
                </Row>
              }
              key="1">
              {block.evaluationResponse && (
                <Row gutter={[16, 16]}>
                  {block.evaluationResponse.evaluation_results.map(
                    (result, index) => (
                      <Col span={24} key={index}>
                        <Card
                          size="small"
                          style={{
                            borderLeft: `4px solid ${
                              result.status === "FAULT"
                                ? "#ff4d4f"
                                : result.status === "PASS"
                                  ? "#52c41a"
                                  : result.status === "NA"
                                    ? "#bfbfbf"
                                    : "#1890ff"
                            }`,
                          }}>
                          <Row
                            gutter={[8, 8]}
                            align="middle"
                            justify="space-between">
                            <Col>
                              <Space size={8} align="center">
                                <Tag
                                  color={
                                    result.status === "FAULT"
                                      ? "red"
                                      : result.status === "PASS"
                                        ? "green"
                                        : result.status === "NA"
                                          ? "default"
                                          : "blue"
                                  }>
                                  {result.sentinel_name}
                                </Tag>
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: "12px" }}>
                                  {`${result.eval_time}ms`}
                                </Typography.Text>
                              </Space>
                            </Col>
                            {companyId === 2 && (
                              <Col>
                                {handleViewRequest(
                                  block.evaluationResponse.evaluation_request_id.toString(),
                                  result.sentinel_id.toString(),
                                )}
                              </Col>
                            )}
                          </Row>
                          {result.description && (
                            <Row>
                              <Col span={24}>
                                <Typography.Paragraph
                                  style={{ marginBottom: 0, marginTop: 8 }}>
                                  {result.description}
                                </Typography.Paragraph>
                              </Col>
                            </Row>
                          )}
                          {result.status === "FAULT" &&
                            result.meta?.correction && (
                              <Row>
                                <Col span={24}>
                                  <blockquote
                                    style={{
                                      margin: "8px 0",
                                      padding: "8px 16px",
                                      borderLeft: "4px solid #1890ff",
                                      backgroundColor: "#f0f5ff",
                                    }}>
                                    <Typography.Text italic>
                                      {result.meta.correction as string}
                                    </Typography.Text>
                                  </blockquote>
                                </Col>
                              </Row>
                            )}
                        </Card>
                      </Col>
                    ),
                  )}
                </Row>
              )}
            </Panel>
          </Collapse>
        </Col>
      ));
  };

  const currentRequest =
  selectedVersion === "original"
    ? request
    : null;

  const loadTestSets = () => {
    TestSetService.getTestSets(
      null,
      apiKey,
    ).subscribe(
      (response: any) => {
        // Group test sets by application_action_id
        const testSets = response || [];
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {},
        );

        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
        message.error("Failed to load test sets");
      },
    );
  };

  const handleTestSetUpdate = (
  ) => {
    setRequest((prevRequest) => {
      if (!prevRequest) return prevRequest;
      loadTestSets();
    });
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  if (isLoading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Card className="request-overview" bodyStyle={{ padding: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}>
          <Title level={3}>Request Overview</Title>
        </div>

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              size="small"
              title={
                <Text strong style={{ fontSize: "16px" }}>
                  {selectedVersion === "original"
                    ? "Original Request"
                    : selectedVersion}
                </Text>
              }
              extra={
                <Space>
                  <Tooltip title="Copy Request ID">
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copyRequestId(
                          selectedRerun
                            ? (selectedRerun.response?.request_id || selectedRerun.originalRequest.request_id)
                            : requestId,
                        )
                      }
                      size="small"
                      type="text"
                    />
                  </Tooltip>
                  {currentRequest && (
                    <TestSetContextMenu
                      applicationId={applicationId}
                      applicationActionType={currentRequest.action_type || ""}
                      requestId={requestId || ""}
                      testSets={
                        currentRequest.action_type
                          ? [
                              ...(testSetsByAction[
                                currentRequest.action_type
                              ] || []),
                            ].sort((a, b) => b.id - a.id)
                          : []
                      }
                      existingTestSetIds={getExistingTestSetIds(
                        currentRequest.metadata,
                      )}
                      onUpdate={handleTestSetUpdate}>
                      <Button
                        icon={<PlusOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </TestSetContextMenu>
                  )}
                </Space>
              }
              style={{ background: "#f8f9fa", borderRadius: "8px" }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Text type="secondary">Request ID</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {selectedRerun
                        ? (selectedRerun.response?.request_id || selectedRerun.originalRequest.request_id)
                        : requestId}
                    </Text>
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Date Created</Text>
                  <div>
                    <Text strong style={{ fontSize: "14px" }}>
                      {moment(
                        selectedRerun
                          ? selectedRerun.originalRequest.date_created
                          : request?.date_created,
                      ).format("MMM D, YYYY HH:mm:ss")}
                    </Text>
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary">Action Type</Text>
                  <div>
                    <Tag color="blue">
                      {selectedRerun
                        ? selectedRerun.originalRequest.action_type
                        : request?.action_type}
                    </Tag>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>{renderVersionContent()}</Col>

          {/* Evaluation blocks */}
          {evaluationBlocks.filter(
            (block) => block.versionId === selectedVersion,
          ).length > 0 && (
            <>
              <Col span={24}>
                <Text
                  style={{ color: "#828282", fontSize: 16, fontWeight: 600 }}>
                  Evaluation
                </Text>
              </Col>
              {renderEvaluationBlocks()}
            </>
          )}

          {/* Add Evaluation button at the bottom */}
          <Col span={24}>
            {isLastMessageAssistant() &&
              !hasUnrunEvaluations() &&
              !isEvaluationRunning() &&
              originalSentinels.length > 0 && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddEvaluation}
                  style={{ width: "100%" }}>
                  Add Evaluation
                </Button>
              )}
          </Col>
        </Row>
      </Card>

      {/* Version selection panel */}
      <div
        style={{
          position: "fixed",
          right: isPanelExpanded ? 0 : -280,
          top: 64,
          bottom: 0,
          width: 300,
          backgroundColor: "#fff",
          boxShadow: "-2px 0 8px rgba(0,0,0,0.15)",
          transition: "right 0.3s ease-in-out",
          zIndex: 1000,
          display: "flex",
        }}
        onMouseEnter={() => setIsPanelExpanded(true)}
        onMouseLeave={() => setIsPanelExpanded(false)}>
        <div
          style={{
            width: 20,
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRight: "1px solid #f0f0f0",
          }}
          onClick={() => setIsPanelExpanded(!isPanelExpanded)}>
          {isPanelExpanded ? <RightOutlined /> : <LeftOutlined />}
        </div>
        <div style={{ flex: 1, padding: "16px", overflowY: "auto" }}>
          <Title level={4}>Versions</Title>
          <Menu
            mode="vertical"
            selectedKeys={[selectedVersion]}
            onClick={({ key }) => setSelectedVersion(key as string)}>
            <Menu.Item key="original">Original</Menu.Item>
            {reruns.map((rerun) => (
              <Menu.Item key={rerun.id}>{rerun.id}</Menu.Item>
            ))}
          </Menu>
          <Button
            type="link"
            onClick={handleRerunRequest}
            style={{ padding: "8px 0", display: "flex", alignItems: "center" }}>
            New Rerun
          </Button>
        </div>
      </div>

      {/* Add this Modal component at the end of the JSX */}
      <Modal
        visible={isEditSentinelModalVisible}
        onCancel={() => {
          setIsEditSentinelModalVisible(false);
          focusedEvaluationBlockRef.current = null;
          selectedSentinelRef.current = undefined;
        }}
        footer={null}
        width={800}
        title="Sentinel"
        closeIcon={<CloseOutlined />}>
        {selectedSentinelRef.current && (
          <EditSentinelForm
            key={`${focusedEvaluationBlockRef.current?.id}-${selectedSentinelRef.current.id}`}
            sentinel={selectedSentinelRef.current}
            loading={false}
            isDataLoaded={true}
            onSubmit={handleEditSentinelSubmit}
            onClose={() => {
              setIsEditSentinelModalVisible(false);
              focusedEvaluationBlockRef.current = null;
              selectedSentinelRef.current = undefined;
            }}
            readOnly={
              focusedEvaluationBlockRef.current
                ? focusedEvaluationBlockRef.current?.hasRun
                : false
            }
          />
        )}
      </Modal>
    </div>
  );
};

export default RequestOverviewScreen;
